import { useSetAtom } from 'jotai';
import { useStore } from 'jotai/react';
import { useCallback } from 'react';
import useFullscreen from '@/hooks/useFullScreen';
import { presentationBoundsAtom, presentationElementIdAtom, presentationModeAtom, roleAtom, } from '@/store/boardAtoms';
import { useResetPresentationState } from './useResetPresentationState';
/**
 * Хук для обработки WebSocket сообщений презентации
 *
 * @param params - Параметры хука
 * @returns Функции для обработки WebSocket сообщений
 */
export const usePresentationWebSocket = ({ onPresentationStart, onPresentationStop, }) => {
    // Store для синхронного чтения атомов вне React-контекста
    const store = useStore();
    const setPresentationMode = useSetAtom(presentationModeAtom);
    const setPresentationElementId = useSetAtom(presentationElementIdAtom);
    const setPresentationBounds = useSetAtom(presentationBoundsAtom);
    const { requestFullscreen, exitFullscreen } = useFullscreen({});
    const resetPresentationState = useResetPresentationState();
    /**
     * Обработка сообщения о начале презентации
     */
    const handlePresentationStart = useCallback((payload) => {
        const startPayload = payload;
        setPresentationMode(true);
        setPresentationElementId(startPayload.elementId);
        setPresentationBounds(startPayload.bounds);
        // Сбрасываем состояние презентации у зрителей
        resetPresentationState();
        // Вызываем дополнительный коллбэк
        onPresentationStart === null || onPresentationStart === void 0 ? void 0 : onPresentationStart();
        if (store.get(roleAtom) !== 'GM') {
            // Пытаемся войти в полноэкранный режим
            requestFullscreen();
        }
    }, [
        setPresentationMode,
        setPresentationElementId,
        setPresentationBounds,
        resetPresentationState,
        onPresentationStart,
        store,
        requestFullscreen,
    ]);
    /**
     * Обработка сообщения об остановке презентации
     */
    const handlePresentationStop = useCallback(() => {
        setPresentationMode(false);
        setPresentationElementId(null);
        setPresentationBounds(null);
        // Сбрасываем состояние презентации у зрителей
        resetPresentationState();
        // Вызываем дополнительный коллбэк
        onPresentationStop === null || onPresentationStop === void 0 ? void 0 : onPresentationStop();
        if (store.get(roleAtom) !== 'GM') {
            // Выходим из полноэкранного режима
            exitFullscreen();
        }
    }, [
        setPresentationMode,
        setPresentationElementId,
        setPresentationBounds,
        resetPresentationState,
        onPresentationStop,
        store,
        exitFullscreen,
    ]);
    /**
     * Обработка сообщения о начале презентации
     */
    const handlePresentationChange = useCallback((payload) => {
        const startPayload = payload;
        setPresentationElementId(startPayload.elementId);
        setPresentationBounds(startPayload.bounds);
        // Сбрасываем состояние презентации у зрителей
        resetPresentationState();
    }, [setPresentationElementId, setPresentationBounds, resetPresentationState]);
    return {
        handlePresentationStart,
        handlePresentationStop,
        handlePresentationChange,
    };
};
